import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { IText } from 'fabric';
import { useDesignStore } from '@/stores/designStore';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import {
  DIRECT_SALE_FONTS,
  ensureFontLoaded,
  type DirectSaleFont,
} from '@/config/direct-sale-fonts';

export const TEXT_SIZE_MIN = 12;
export const TEXT_SIZE_MAX = 60;
/** What the controls show while no text is bound. */
const DEFAULT_SIZE = 30;

export interface DirectSaleTextStyle {
  /** A text object is selected and bound. */
  isBound: boolean;
  fontFamily: string | null;
  /** Rounded, within min/max. */
  fontSize: number;
  /** Hex as stored on the object. */
  color: string | null;
  /** Loads the font, then applies it; one history step. */
  setFont: (fontId: string) => Promise<void>;
  /** Live while dragging the slider; no history step. */
  setSizeLive: (size: number) => void;
  /** On release; exactly one history step for the whole drag. */
  commitSize: () => void;
  /** One history step. */
  setColor: (hex: string) => void;
}

/** The one check of the desktop for "this object is a text". */
export function isTextObject(object: { type?: string }): boolean {
  return (
    object.type === 'textbox' ||
    object.type === 'i-text' ||
    object.type === 'text'
  );
}

function clampSize(size: number): number {
  return Math.min(Math.max(Math.round(size), TEXT_SIZE_MIN), TEXT_SIZE_MAX);
}

/**
 * Fabric's corner handle scales a text and leaves fontSize untouched, so the
 * size the customer sees is fontSize times the scale.
 */
function scaleOf(target: IText): number {
  const scale = target.scaleY ?? 1;
  return scale > 0 ? scale : 1;
}

/** A font by its grid name ("Inter Bold") or by its family ("Inter"). */
function findFont(fontId: string): DirectSaleFont | null {
  return (
    DIRECT_SALE_FONTS.find(
      (f) => f.label === fontId || f.fontFamily === fontId
    ) ?? null
  );
}

/**
 * Identifies the newest history step; the stack length stalls at its cap.
 * Cross-store, so guarded: a history that cannot be read reads as "no step",
 * and the style change goes on instead of breaking.
 */
function latestHistoryStep(): string | null {
  try {
    const { undoStack } = useCanvasStateManager.getState();
    return undoStack[undoStack.length - 1]?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * The desktop's text styling: font, size and colour of the selected text,
 * without DirectSaleTextPanel. The desktop changes the wording on the product
 * itself, so this hook never creates or removes a text.
 *
 * Several controls (object bar, font list, colour grid) each call this hook.
 * They stay in step because every value is read from the bound object and
 * every change is announced on the canvas, which all of them listen to.
 */
export function useDirectSaleTextStyle(): DirectSaleTextStyle {
  const canvas = useDesignStore((s) => s.canvas);
  const selectedObjects = useDesignStore((s) => s.selectedObjects);
  const saveToHistory = useDesignStore((s) => s.saveToHistory);

  const bound = useMemo(
    () => (selectedObjects.find(isTextObject) as IText | undefined) ?? null,
    [selectedObjects]
  );

  // Fabric objects change in place; React only notices when told to.
  const [, refresh] = useReducer((n: number) => n + 1, 0);

  /** The slider moved since the last history step. */
  const sizeDirtyRef = useRef(false);
  useEffect(() => {
    // An open drag belongs to the previous text.
    sizeDirtyRef.current = false;
  }, [bound]);

  // Canvas-side changes (scaling by the handle, another control of this
  // text) reach the object without React noticing — listen and re-read.
  useEffect(() => {
    if (!canvas || !bound) return;
    const sync = (e: { target?: unknown }) => {
      if (e.target !== bound) return;
      refresh();
    };
    canvas.on('text:changed', sync);
    canvas.on('object:modified', sync);
    return () => {
      canvas.off('text:changed', sync);
      canvas.off('object:modified', sync);
    };
  }, [canvas, bound]);

  /**
   * Announce the change and make sure it costs exactly one history step. The
   * object:modified fire feeds the step's masterPositions save. The editor
   * answers the same event with a history step of its own; writing a second
   * one on top would make the first undo look like it did nothing. Where
   * nobody wrote one, the step is saved here (cross-store, so guarded — a
   * failure must never break the edit).
   */
  const commit = useCallback(
    (target: IText) => {
      const before = latestHistoryStep();
      try {
        canvas?.fire('object:modified', { target });
      } catch {
        // Listeners unavailable; the canvas content itself is already updated.
      }
      if (latestHistoryStep() !== before) return;
      try {
        saveToHistory('text-edit');
      } catch {
        // History unavailable; persistence was already triggered above.
      }
    },
    [canvas, saveToHistory]
  );

  const setFont = useCallback(
    async (fontId: string) => {
      const picked = findFont(fontId);
      if (!picked || !bound || !canvas) return;
      // Fabric measures glyphs at render time — apply only once the face is
      // loaded, else the object renders (and previews export) with fallback
      // metrics.
      await ensureFontLoaded(picked.fontFamily, picked.fontWeight);
      // The load can resolve after the object was deleted or deselected —
      // mutating a detached object would only write a dead history entry.
      if (!canvas.getObjects().includes(bound)) return;
      bound.set({
        fontFamily: picked.fontFamily,
        fontWeight: picked.fontWeight,
      });
      canvas.requestRenderAll();
      commit(bound);
      refresh();
    },
    [bound, canvas, commit]
  );

  const setSizeLive = useCallback(
    (size: number) => {
      if (!bound || !canvas) return;
      bound.set('fontSize', clampSize(size) / scaleOf(bound));
      canvas.requestRenderAll();
      sizeDirtyRef.current = true;
      refresh();
    },
    [bound, canvas]
  );

  const commitSize = useCallback(() => {
    // Releasing a slider that never moved (a Tab through it, the emulated
    // mouseup after a touchend) changed nothing and must not cost an undo.
    // The controls hand every release on; this is the only guard.
    if (!bound || !sizeDirtyRef.current) return;
    sizeDirtyRef.current = false;
    commit(bound);
  }, [bound, commit]);

  const setColor = useCallback(
    (hex: string) => {
      if (!bound || !canvas) return;
      bound.set('fill', hex);
      canvas.requestRenderAll();
      commit(bound);
      refresh();
    },
    [bound, canvas, commit]
  );

  return {
    isBound: bound !== null,
    fontFamily: bound?.fontFamily ?? null,
    fontSize: bound
      ? clampSize((bound.fontSize ?? DEFAULT_SIZE) * scaleOf(bound))
      : DEFAULT_SIZE,
    color: typeof bound?.fill === 'string' ? bound.fill : null,
    setFont,
    setSizeLive,
    commitSize,
    setColor,
  };
}
